"use client"
import { motion } from "framer-motion"
import {
  Lightbulb,
  Shield,
  Users,
  Heart,
  Monitor,
  Settings,
  Zap,
  Headphones,
  Star,
  Award,
  Target,
  Rocket,
} from "lucide-react"
import Header from "../components/Header"
import { Link } from "react-router-dom"
import Footer from "../components/Footer"

export const About = () => {
  const pageBnrImg = {
    position: "relative",
    backgroundImage: `url('./media/frontend/img/about/blog/1920x700/banner-1.jpg')`,
    backgroundPosition: "center",
    backgroundSize: "cover",
  }

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, ease: [0.6, -0.05, 0.01, 0.99] },
  }

  const fadeInLeft = {
    initial: { opacity: 0, x: -80 },
    animate: { opacity: 1, x: 0 },
    transition: { duration: 0.8, ease: [0.6, -0.05, 0.01, 0.99] },
  }

  const fadeInRight = {
    initial: { opacity: 0, x: 80 },
    animate: { opacity: 1, x: 0 },
    transition: { duration: 0.8, ease: [0.6, -0.05, 0.01, 0.99] },
  }

  const scaleIn = {
    initial: { opacity: 0, scale: 0.8 },
    animate: { opacity: 1, scale: 1 },
    transition: { duration: 0.6, ease: "easeOut" },
  }

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.15,
      },
    },
  }

  const cardHover = {
    scale: 1.08,
    y: -15,
    rotateY: 5,
    transition: {
      duration: 0.4,
      ease: [0.6, -0.05, 0.01, 0.99],
      type: "spring",
      stiffness: 300,
    },
  }

  const iconFloat = {
    y: [-10, 10, -10],
    transition: {
      duration: 3,
      repeat: Number.POSITIVE_INFINITY,
      ease: "easeInOut",
    },
  }

  const valuesData = [
    {
      icon: Lightbulb,
      number: "01",
      title: "Innovation",
      description: "Continuously evolving to meet the ever-changing needs of the culinary world.",
      color: "from-blue-500 to-purple-600",
      bgColor: "from-blue-50 to-purple-50",
    },
    {
      icon: Shield,
      number: "02",
      title: "Reliability",
      description: "Offering secure, scalable, and user-friendly solutions you can depend on.",
      color: "from-green-500 to-teal-600",
      bgColor: "from-green-50 to-teal-50",
    },
    {
      icon: Users,
      number: "03",
      title: "Collaboration",
      description: "Building a community where culinary professionals can thrive together.",
      color: "from-orange-500 to-red-600",
      bgColor: "from-orange-50 to-red-50",
    },
    {
      icon: Heart,
      number: "04",
      title: "Passion",
      description: "Celebrating the art and science of food at every stage of the journey.",
      color: "from-pink-500 to-rose-600",
      bgColor: "from-pink-50 to-rose-50",
    },
  ]

  const whyChooseData = [
    {
      icon: Monitor,
      number: "01",
      title: "User-Friendly Interface",
      description: "Designed for shefs and teams of all skill levels.",
      color: "from-indigo-500 to-blue-600",
      bgColor: "from-indigo-50 to-blue-50",
    },
    {
      icon: Settings,
      number: "02",
      title: "Custom Solutions",
      description: "Tailored features to suit your unique needs.",
      color: "from-purple-500 to-indigo-600",
      bgColor: "from-purple-50 to-indigo-50",
    },
    {
      icon: Zap,
      number: "03",
      title: "Real-Time Accessibility",
      description: "Manage your operations anytime, anywhere.",
      color: "from-yellow-500 to-orange-600",
      bgColor: "from-yellow-50 to-orange-50",
    },
    {
      icon: Headphones,
      number: "04",
      title: "Ongoing Support",
      description: "Celebrating the art and science of food at every stage of the journey.",
      color: "from-teal-500 to-green-600",
      bgColor: "from-teal-50 to-green-50",
    },
  ]

  return (
    <>
      <Header />

      {/* Enhanced Hero Banner Section */}
      <section className="relative overflow-hidden">
        <div className="pageBanr py-32 relative" style={pageBnrImg}>
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/70"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/20 to-black/60"></div>

          {/* Animated background elements */}
          <div className="absolute inset-0 overflow-hidden">
            <motion.div
              className="absolute top-20 left-10 w-2 h-2 bg-primary rounded-full"
              animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 2, repeat: Number.POSITIVE_INFINITY }}
            />
            <motion.div
              className="absolute top-40 right-20 w-3 h-3 bg-white rounded-full"
              animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.8, 0.3] }}
              transition={{ duration: 3, repeat: Number.POSITIVE_INFINITY, delay: 1 }}
            />
            <motion.div
              className="absolute bottom-32 left-1/4 w-1 h-1 bg-primary rounded-full"
              animate={{ scale: [1, 2, 1], opacity: [0.4, 1, 0.4] }}
              transition={{ duration: 2.5, repeat: Number.POSITIVE_INFINITY, delay: 0.5 }}
            />
          </div>

          <motion.div
            className="container mx-auto relative z-[1] text-center px-4"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
          >
            <motion.div
              className="inline-block mb-6"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.8, delay: 0.5, type: "spring" }}
            >
              <div className="w-20 h-20 bg-gradient-to-br from-primary/20 to-primary/40 rounded-full flex items-center justify-center backdrop-blur-sm border border-white/20">
                <Star className="w-10 h-10 text-primary" />
              </div>
            </motion.div>

            <motion.h2
              className="font-bold text-5xl md:text-7xl text-white uppercase tracking-wider mb-6 bg-gradient-to-r from-white via-gray-100 to-white bg-clip-text"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              About Us
            </motion.h2>

            <motion.div
              className="w-32 h-1 bg-gradient-to-r from-transparent via-primary to-transparent mx-auto mb-8"
              initial={{ width: 0 }}
              animate={{ width: 128 }}
              transition={{ duration: 1, delay: 0.8 }}
            />

            <motion.ul
              className="inline-flex items-center space-x-2 text-white mb-0 bg-black/20 backdrop-blur-sm rounded-full px-6 py-3 border border-white/10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1 }}
            >
              <li className="text-lg">
                <Link
                  to="/"
                  className="!text-white hover:!text-primary transition-all duration-300 hover:scale-110 inline-block"
                >
                  Home
                </Link>
              </li>
              <li className="text-white/60">/</li>
              <li className="text-lg font-semibold text-primary">About Us</li>
            </motion.ul>
          </motion.div>
        </div>
      </section>

      {/* Enhanced About Us Section */}
      <section className="aboutus py-24 bg-gradient-to-br from-white via-gray-50/50 to-white">
        <div className="container mx-auto lg:px-2 px-4">
          <div className="grid lg:grid-cols-2 grid-cols-1 gap-16 items-center">
            <motion.div
              className="my-auto"
              initial="initial"
              whileInView="animate"
              viewport={{ once: true, margin: "-100px" }}
              variants={fadeInLeft}
            >
              <div className="history-title mb-8">
                <motion.div className="inline-flex items-center space-x-3 mb-6" variants={scaleIn}>
                  <div className="w-12 h-12 bg-gradient-to-br from-primary/10 to-primary/20 rounded-xl flex items-center justify-center">
                    <Award className="w-6 h-6 text-primary" />
                  </div>
                  <span className="text-primary font-semibold text-lg uppercase tracking-wider">Our Story</span>
                </motion.div>

                <motion.h1
                  className="font-bold text-4xl lg:text-6xl uppercase text-secondary tracking-wider lg:text-start text-center mb-8 leading-tight"
                  variants={fadeInUp}
                >
                  A History Written for{" "}
                  <span className="bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                    Shefs Cloud
                  </span>{" "}
                  Explore : Our Story
                </motion.h1>

                <motion.div
                  className="w-24 h-1 bg-gradient-to-r from-primary via-primary/60 to-transparent my-8 lg:mx-0 mx-auto"
                  variants={fadeInUp}
                />

                <div className="space-y-6">
                  <motion.p
                    className="text-xl text-headGray lg:text-start text-center leading-relaxed font-medium"
                    variants={fadeInUp}
                  >
                    Shefs Cloud is where culinary innovations meet technology. With Shefs Cloud, shefs, culinary
                    businesses, and food enthusiasts can access advanced cloud-based solutions tailored to their needs.
                  </motion.p>
                  <motion.p
                    className="text-lg text-headGray lg:text-start text-center leading-relaxed"
                    variants={fadeInUp}
                  >
                    We believe seamless, efficient, and intelligent digital solutions are the way forward for the food
                    industry. Using our platform, businesses can simplify operations, improve productivity, and focus on
                    what matters most: creating delicious food.
                  </motion.p>
                  <motion.p
                    className="text-lg text-headGray lg:text-start text-center leading-relaxed"
                    variants={fadeInUp}
                  >
                    With Shefs Cloud, we're revolutionizing the culinary landscape through technology. By simplifying
                    operations and reducing complexity, we help businesses and individuals grow. Shefs Cloud is here to
                    help you succeed, whether you're a shef, entrepreneur, or culinary enthusiast.
                  </motion.p>
                </div>
              </div>
            </motion.div>

            <motion.div
              className="relative"
              initial="initial"
              whileInView="animate"
              viewport={{ once: true, margin: "-100px" }}
              variants={fadeInRight}
            >
              {/* Decorative background elements */}
              <div className="absolute -top-10 -right-10 w-32 h-32 bg-gradient-to-br from-primary/10 to-transparent rounded-full blur-2xl" />
              <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-gradient-to-br from-primary/5 to-transparent rounded-full blur-3xl" />

              <div className="grid grid-cols-2 gap-6 relative z-10">
                <motion.div className="my-auto" variants={fadeInUp}>
                  <div className="relative group overflow-hidden rounded-3xl shadow-2xl">
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent z-10" />
                    <motion.img
                      src="/media/frontend/img/about/blog/255x200/about-section-3.jpg"
                      className="img-fluid full-width transition-all duration-700 group-hover:scale-110"
                      alt="Histry"
                      whileHover={{ scale: 1.05 }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-20" />
                  </div>
                </motion.div>

                <motion.div className="grid grid-cols-1 gap-6" variants={staggerContainer}>
                  <motion.div className="relative group overflow-hidden rounded-3xl shadow-2xl" variants={fadeInUp}>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent z-10" />
                    <motion.img
                      src="/media/frontend/img/about/blog/255x200/about-section-1.jpg"
                      className="img-fluid full-width transition-all duration-700 group-hover:scale-110"
                      alt="Histry"
                      whileHover={{ scale: 1.05 }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-20" />
                  </motion.div>

                  <motion.div className="relative group overflow-hidden rounded-3xl shadow-2xl" variants={fadeInUp}>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent z-10" />
                    <motion.img
                      src="/media/frontend/img/about/blog/255x200/about-section-2.jpg"
                      className="img-fluid full-width transition-all duration-700 group-hover:scale-110"
                      alt="Histry"
                      whileHover={{ scale: 1.05 }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-20" />
                  </motion.div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Enhanced Values Section */}
      <section className="py-24 bg-gradient-to-br from-gray-50 via-white to-gray-50 relative overflow-hidden">
        {/* Background decorations */}
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-20 left-10 w-64 h-64 bg-gradient-to-br from-primary/5 to-transparent rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-80 h-80 bg-gradient-to-br from-primary/3 to-transparent rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto lg:px-2 px-4 relative z-10">
          <motion.div
            className="text-center mb-20"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <motion.div className="inline-flex items-center space-x-3 mb-6" variants={scaleIn}>
              <div className="w-12 h-12 bg-gradient-to-br from-primary/10 to-primary/20 rounded-xl flex items-center justify-center">
                <Target className="w-6 h-6 text-primary" />
              </div>
              <span className="text-primary font-semibold text-lg uppercase tracking-wider">What Drives Us</span>
            </motion.div>

            <h2 className="font-bold text-5xl lg:text-6xl uppercase tracking-wider mb-6">
              Our{" "}
              <span className="bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">Values</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-primary via-primary/60 to-transparent mx-auto mb-8" />
            <p className="text-xl text-headGray max-w-3xl mx-auto leading-relaxed">
              At Shefs Cloud, we are guided by core principles that shape everything we do
            </p>
          </motion.div>

          <motion.div
            className="grid lg:grid-cols-4 sm:grid-cols-2 grid-cols-1 gap-8"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {valuesData.map((item, index) => {
              const IconComponent = item.icon
              return (
                <motion.div key={index} className="group" variants={fadeInUp}>
                  <motion.div
                    className={`relative bg-gradient-to-br ${item.bgColor} rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 p-8 h-full border border-white/50 backdrop-blur-sm overflow-hidden`}
                    whileHover={cardHover}
                  >
                    {/* Background decoration */}
                    <div
                      className={`absolute -top-10 -right-10 w-32 h-32 bg-gradient-to-br ${item.color} opacity-10 rounded-full blur-2xl group-hover:opacity-20 transition-opacity duration-500`}
                    />

                    <div className="relative z-10">
                      <div className="flex items-center justify-between mb-6">
                        <motion.div
                          className={`w-16 h-16 bg-gradient-to-br ${item.color} rounded-2xl flex items-center justify-center shadow-lg`}
                          animate={iconFloat}
                        >
                          <IconComponent className="w-8 h-8 text-white" />
                        </motion.div>
                        <span
                          className={`w-12 h-12 bg-gradient-to-br ${item.color} text-white rounded-full flex items-center justify-center text-lg font-bold shadow-lg`}
                        >
                          {item.number}
                        </span>
                      </div>

                      <h6 className="font-bold text-2xl uppercase mb-4 text-secondary group-hover:text-gray-800 transition-colors duration-300">
                        {item.title}
                      </h6>
                      <p className="text-lg text-headGray leading-relaxed group-hover:text-gray-700 transition-colors duration-300">
                        {item.description}
                      </p>
                    </div>
                  </motion.div>
                </motion.div>
              )
            })}
          </motion.div>

          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="mt-16 text-center max-w-4xl mx-auto space-y-6"
          >
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/50">
              <p className="text-lg text-headGray leading-relaxed mb-4">
                It all started with a desire to make the culinary world smarter and more connected. In order to manage
                recipes, inventory, and operations efficiently, our founders recognized the challenges shefs and food
                businesses face. Shefs can use Shefs Cloud to streamline workflows, unlock creativity, and scale their
                businesses by bridging the gap between technology and culinary arts.
              </p>
              <p className="text-lg text-headGray leading-relaxed">
                We're proud to help culinary professionals embrace the future of food with confidence and ease today.
              </p>
            </div>
          </motion.div>
        </div>

        {/* Enhanced Why Choose Us Section */}
        <div className="container mx-auto lg:px-2 px-4 mt-24 relative z-10">
          <motion.div
            className="text-center mb-20"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <motion.div className="inline-flex items-center space-x-3 mb-6" variants={scaleIn}>
              <div className="w-12 h-12 bg-gradient-to-br from-primary/10 to-primary/20 rounded-xl flex items-center justify-center">
                <Rocket className="w-6 h-6 text-primary" />
              </div>
              <span className="text-primary font-semibold text-lg uppercase tracking-wider">Our Advantage</span>
            </motion.div>

            <h2 className="font-bold text-5xl lg:text-6xl uppercase tracking-wider mb-6">
              Why Choose{" "}
              <span className="bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">Us</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-primary via-primary/60 to-transparent mx-auto mb-8" />
          </motion.div>

          <motion.div
            className="grid lg:grid-cols-4 sm:grid-cols-2 grid-cols-1 gap-8"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {whyChooseData.map((item, index) => {
              const IconComponent = item.icon
              return (
                <motion.div key={index} className="flex group" variants={fadeInUp}>
                  <motion.div
                    className={`relative bg-gradient-to-br ${item.bgColor} rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 p-8 flex-1 border border-white/50 backdrop-blur-sm overflow-hidden`}
                    whileHover={cardHover}
                  >
                    {/* Background decoration */}
                    <div
                      className={`absolute -top-10 -right-10 w-32 h-32 bg-gradient-to-br ${item.color} opacity-10 rounded-full blur-2xl group-hover:opacity-20 transition-opacity duration-500`}
                    />

                    <div className="relative z-10">
                      <div className="flex items-center justify-between mb-6">
                        <motion.div
                          className={`w-16 h-16 bg-gradient-to-br ${item.color} rounded-2xl flex items-center justify-center shadow-lg`}
                          animate={iconFloat}
                        >
                          <IconComponent className="w-8 h-8 text-white" />
                        </motion.div>
                        <span
                          className={`w-12 h-12 bg-gradient-to-br ${item.color} text-white rounded-full flex items-center justify-center text-lg font-bold shadow-lg`}
                        >
                          {item.number}
                        </span>
                      </div>

                      <h6 className="font-bold text-2xl uppercase mb-4 text-secondary group-hover:text-gray-800 transition-colors duration-300">
                        {item.title}
                      </h6>
                      <p className="text-lg text-headGray leading-relaxed group-hover:text-gray-700 transition-colors duration-300">
                        {item.description}
                      </p>
                    </div>
                  </motion.div>
                </motion.div>
              )
            })}
          </motion.div>

          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="mt-16 text-center max-w-4xl mx-auto space-y-6"
          >
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/50">
              <p className="text-lg text-headGray leading-relaxed mb-4">
                Come join us on our journey. Shefs Cloud is more than a platform; it's a community of food lovers,
                innovators, and dreamers. Let's simplify operations, inspire creativity, and change the culinary world
                together.
              </p>
              <p className="text-lg text-headGray leading-relaxed">
                Are you ready to take your culinary business to the next level? Try Shefs Cloud today!
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Enhanced CTA Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-black to-gray-900">
        {/* Animated background */}
        <div className="absolute inset-0">
          <motion.div
            className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-3xl"
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.5, 0.3],
            }}
            transition={{
              duration: 4,
              repeat: Number.POSITIVE_INFINITY,
              ease: "easeInOut",
            }}
          />
          <motion.div
            className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-primary/15 rounded-full blur-3xl"
            animate={{
              scale: [1.2, 1, 1.2],
              opacity: [0.2, 0.4, 0.2],
            }}
            transition={{
              duration: 5,
              repeat: Number.POSITIVE_INFINITY,
              ease: "easeInOut",
              delay: 1,
            }}
          />
        </div>

        <div className="relative z-10">
          <div className="py-32">
            <div className="container mx-auto lg:px-2 px-4">
              <motion.div
                className="text-center max-w-4xl mx-auto"
                initial="initial"
                whileInView="animate"
                viewport={{ once: true }}
                variants={fadeInUp}
              >
                <motion.div className="inline-flex items-center space-x-3 mb-8" variants={scaleIn}>
                  <div className="w-16 h-16 bg-gradient-to-br from-primary/20 to-primary/40 rounded-2xl flex items-center justify-center backdrop-blur-sm border border-white/10">
                    <Star className="w-8 h-8 text-primary" />
                  </div>
                </motion.div>

                <motion.h2 className="font-bold text-4xl text-white uppercase tracking-wider mb-6" variants={fadeInUp}>
                  Shefs Cloud
                </motion.h2>

                <motion.div
                  className="w-32 h-1 bg-gradient-to-r from-transparent via-primary to-transparent mx-auto mb-8"
                  variants={fadeInUp}
                />

                <motion.h3
                  className="font-bold text-6xl lg:text-7xl text-white uppercase tracking-wider mb-8 bg-gradient-to-r from-white via-gray-100 to-white bg-clip-text"
                  variants={fadeInUp}
                >
                  Variety of Dishes
                </motion.h3>

                <motion.p
                  className="text-2xl text-white/90 leading-relaxed mb-12 max-w-2xl mx-auto"
                  variants={fadeInUp}
                >
                  Book a dish easily at the best price
                </motion.p>

                <motion.div variants={fadeInUp}>
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="inline-block">
                    <Link
                      to="/all-dish-detail"
                      className="group relative inline-flex items-center space-x-3 rounded-full py-6 px-12 text-xl tracking-wider uppercase font-bold bg-gradient-to-r from-primary via-primary to-primary/80 !text-white border-2 border-transparent hover:border-primary hover:bg-transparent hover:text-primary transition-all duration-500 shadow-2xl hover:shadow-primary/25 overflow-hidden"
                    >
                      <span className="relative z-10">View All</span>
                      <motion.div
                        className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center"
                        whileHover={{ rotate: 360 }}
                        transition={{ duration: 0.5 }}
                      >
                        <Rocket className="w-4 h-4" />
                      </motion.div>
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 transform translate-x-full group-hover:translate-x-[-200%] transition-transform duration-1000" />
                    </Link>
                  </motion.div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  )
}

export default About
